/** Angular */
import { CommonModule, CurrencyPipe, DatePipe, PercentPipe, TitleCasePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, NgZone, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, DomSanitizer, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

/** Firebase */
import { initializeApp as initializeFirebaseApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';

/** Material */
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

/** Third-party */
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { Apollo, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { DpDatePickerModule } from 'ng2-date-picker';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Observable } from 'rxjs';

/** Foundation */
import { InitService, ZendeskWidgetService } from '@aiwyn/services';
import packageJson from '../../package.json';
import { environment, ENVIRONMENT_PROVIDER } from '../environments';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/main/header/header.component';
import { EmptyPageComponent } from './components/main/invoices/empty-page/empty-page.component';
import { MainComponent } from './components/main/main.component';
import { ExtendedApollo } from './config/services/extended-apollo.service';
import { InitializationData } from './model';
import { AppState } from './state';
import { CxpTasksState } from './features/cxp/state/cxp-tasks.state';

/** Aiwyn Libraries */
import { UploadApiService } from '@aiwyn/api';
import {
  AUTH_PROVIDER_BINDINGS,
  AuthInterceptor,
  AuthModule,
  AuthProviderToken,
  AuthService,
  PENDO_TRACKER,
  provideAuthWorkflowTracking,
  UsersnapService,
} from '@aiwyn/auth';
import { CommonLayoutModule } from '@aiwyn/common/layout';
import { PaymentsModule, providePaymentsWorkflowTracking } from '@aiwyn/common/payments';
import { CommonSettingsModule } from '@aiwyn/common/settings';
import {
  AssetUriService,
  ConfigModule,
  ConfigService,
  ConfigState,
  Globals,
  LaunchDarklyService,
  LOG_ROCKET_TRACKER,
  provideLogging,
  provideSessionReplayCorrelation,
} from '@aiwyn/config';
import {
  AbstractUploadApiService,
  APP_VERSION_TOKEN,
  CUSTOMER_ASSET_URL,
  IconRegistryService,
  Logger,
  provideTracking,
  SharedModule,
  SharedState,
  TOKEN_REFRESHER,
} from '@aiwyn/shared';
import {
  ConsentStepComponent,
  EngagementCardItemComponent,
  EngagementCompleteDialogComponent,
  EngagementErrorDialogComponent,
  EngagementLetterStepComponent,
  EngagementsCardListComponent,
  EngagementsContainerComponent,
  EngagementsRoutingModule,
  EngagementsState,
  EngagementsTableComponent,
  PaymentInfoStepComponent,
  ProvideOrDenyConsentDialogComponent,
  ViewEngagementContainerComponent,
} from '@aiwyn/engagements';
import { HighlightPipe } from './shared/pipes';

/** Interceptors */
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { XhrInterceptor } from './interceptors/xhr.interceptor';
import { CacheErrorHandler } from './interceptors/cache-error-handler.service';

/** Auth */
import { PasswordResetEmailComponent } from './components/auth/check-email/password-reset/password-reset-email.component';
import { VerifyEmailComponent } from './components/auth/check-email/verification/verify-email.component';
import { ClientKeyComponent } from './components/auth/client-key/client-key.component';
import { ClientPortalHeaderComponent } from './components/auth/client-portal-header/client-portal-header.component';
import { LoginBannerComponent } from './components/auth/login-banner/login-banner.component';
import { AppLoginComponent } from './components/auth/login-components/app-login/app-login.component';
import { AppRegisterComponent } from './components/auth/login-components/app-register/app-register.component';
import { RegisterContactComponent } from './components/auth/login-components/register-contact/register-contact.component';
import { LoginGetHelpComponent } from './components/auth/login-get-help/login-get-help.component';
import { LoginComponent } from './components/auth/login-page/login.component';
import { LoginPasswordlessExpiredComponent } from './components/auth/login-passwordless-expired/login-passwordless-expired.component';
import { LoginPasswordlessWaitComponent } from './components/auth/login-passwordless-wait/login-passwordless-wait.component';
import { LoginPasswordlessComponent } from './components/auth/login-passwordless/login-passwordless.component';
import { LogoutComponent } from './components/auth/logout/logout.component';
import { RegisterComponent } from './components/auth/registration-page/register.component';
import { ResetPasswordWaitComponent } from './components/auth/reset-password-wait/reset-password-wait.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import { SessionExpiredDialogComponent } from './components/auth/session-expired-dialog/session-expired-dialog.component';
import { UnderMaintenancePageComponent } from './components/auth/under-maintenance-page/under-maintenance-page.component';
import { InvalidPasswordComponent } from './components/main/modals/invalid-password/invalid-password.component';
import {
  MultipleSessionsDetectedDialogComponent,
} from './components/main/modals/multiple-sessions-detected-dialog/multiple-sessions-detected-dialog.component';
import { NotRegisteredComponent } from './components/main/modals/not-registered/not-registered.component';

/** Payments */
import { InvoiceMobileCardComponent } from './components/main/invoices/invoice-table-card/invoice-mobile-card.component';
import { InvoicesComponent } from './components/main/invoices/invoices.component';
import { UnpaidMobileComponent } from './components/main/invoices/unpaid/unpaid-mobile/unpaid-mobile.component';
import { UnpaidComponent } from './components/main/invoices/unpaid/unpaid.component';
import {
  CannotDeletePaymentMethodComponent,
} from './components/main/modals/cannot-delete-payment-method/cannot-delete-payment-method.component';
import {
  ConfirmDeletePaymentPlanModalComponent,
} from './components/main/modals/confirm-delete-payment-plan-modal/confirm-delete-payment-plan-modal.component';
import { ConfirmDeleteComponent } from './components/main/modals/confirm-delete/confirm-delete.component';
import {
  PaymentMethodIsNotFromUserComponent,
} from './components/main/modals/payment-method-is-not-from-user/payment-method-is-not-from-user.component';
import { PaymentPlanModalComponent } from './components/main/modals/payment-plan-modal/payment-plan-modal.component';
import { PaymentResultUnknownDialogComponent } from './components/main/modals/payment-unknown/payment-unknown.component';
import { PdfModalComponent } from './components/main/modals/pdf-modal/pdf-modal.component';
import {
  RemoveOtherUserPaymentMethodComponent,
} from './components/main/modals/remove-other-user-payment-method/remove-other-user-payment-method.component';
import { ExpandableInvoiceListComponent } from './components/main/payments/paid/expandable-invoice-list/expandable-invoice-list.component';
import { PaidComponent } from './components/main/payments/paid/paid.component';
import { PaymentsComponent } from './components/main/payments/payments.component';
import { ScheduledTableCardComponent } from './components/main/payments/scheduled/scheduled-table-card/scheduled-table-card.component';
import { ScheduledComponent } from './components/main/payments/scheduled/scheduled.component';
import { TaxPaymentsComponent } from './components/main/payments/tax-payments/tax-payments.component';
import { InvoiceCardComponent } from './components/main/review-and-pay/multi-invoice-view/invoice-card/invoice-card.component';
import { MultiInvoiceViewComponent } from './components/main/review-and-pay/multi-invoice-view/multi-invoice-view.component';
import { ReviewAndPayComponent } from './components/main/review-and-pay/review-and-pay.component';
import { SingleInvoiceViewComponent } from './components/main/review-and-pay/single-invoice-view/single-invoice-view.component';
import { PaymentPlansComponent } from './components/main/settings/payment-plans/payment-plans.component';
import { OpenPortalComponent, QuickPaymentLinkErrorPageComponent } from './components/open-portal';

/** Settings */
import { AccountSettingsComponent } from './components/main/account-settings/account-settings.component';
import { AddEditAutopayDialogComponent } from './components/main/modals/add-edit-autopay-dialog/add-edit-autopay-dialog.component';
import { AutoPaySettingsComponent } from './components/main/settings/auto-pay-settings/auto-pay-settings.component';
import { DeleteAutoPayDialogComponent } from './components/main/settings/delete-auto-pay-dialog/delete-auto-pay-dialog.component';
import { GeneralComponent } from './components/main/settings/general/general.component';
import { SavedPaymentMethodsComponent } from './components/main/settings/saved-payment-methods/saved-payment-methods.component';
import { SettingsComponent } from './components/main/settings/settings.component';
import { SettingsState } from './components/main/settings/state';

/** Modals */
import { ModalAlertComponent } from './components/main/modals/alert/modal-alert.component';
import { CacheErrorModalComponent } from './components/main/modals/cache-error-modal/cache-error-modal.component';
import { GenericErrorComponent } from './components/main/modals/generic-error/generic-error.component';

/** Pipes */
import { AddPaymentFeesPipe } from './pipe/add-payment-fees.pipe';

/** Misc. */
import { MatDivider } from '@angular/material/divider';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { NgxMaskDirective } from 'ngx-mask';
import { CustomPaginatorIntl } from './components/main/classes/custom-paginator-intl';
import { InvoicesTableSummaryComponent } from './components/main/invoices/invoices-table-summary/invoices-table-summary.component';
import { DebounceClickDirective } from './directives/debounce-click.directive';

/**
 * Initialize application. InitializationData includes ConfigOptions.
 */
function initializeApp(initService: InitService): () => Observable<InitializationData> {
  return () => initService.initializeApplication();
}

let UniversalDeviceDetectorService;

@NgModule({
  declarations: [
    // Foundation
    AppComponent,
    EmptyPageComponent,
    HeaderComponent,
    MainComponent,
    OpenPortalComponent,

    // Auth
    AppLoginComponent,
    AppRegisterComponent,
    ClientKeyComponent,
    ClientPortalHeaderComponent,
    LoginBannerComponent,
    LoginComponent,
    LoginGetHelpComponent,
    LoginPasswordlessComponent,
    LoginPasswordlessExpiredComponent,
    LoginPasswordlessWaitComponent,
    LogoutComponent,
    PasswordResetEmailComponent,
    RegisterComponent,
    RegisterContactComponent,
    ResetPasswordComponent,
    ResetPasswordWaitComponent,
    SessionExpiredDialogComponent,
    UnderMaintenancePageComponent,
    VerifyEmailComponent,

    // Payments
    CannotDeletePaymentMethodComponent,
    ConfirmDeleteComponent,
    ConfirmDeletePaymentPlanModalComponent,
    ExpandableInvoiceListComponent,
    InvoiceCardComponent,
    InvoiceMobileCardComponent,
    InvoicesComponent,
    InvoicesTableSummaryComponent,
    MultiInvoiceViewComponent,
    PaidComponent,
    PaymentMethodIsNotFromUserComponent,
    PaymentPlanModalComponent,
    PaymentPlansComponent,
    PaymentResultUnknownDialogComponent,
    PaymentsComponent,
    RemoveOtherUserPaymentMethodComponent,
    ReviewAndPayComponent,
    ScheduledComponent,
    ScheduledTableCardComponent,
    SingleInvoiceViewComponent,
    TaxPaymentsComponent,
    UnpaidComponent,
    UnpaidMobileComponent,

    // Settings
    AccountSettingsComponent,
    AddEditAutopayDialogComponent,
    AutoPaySettingsComponent,
    DeleteAutoPayDialogComponent,
    SavedPaymentMethodsComponent,
    SettingsComponent,
    GeneralComponent,

    // UI Components
    DebounceClickDirective,

    // Modals
    CacheErrorModalComponent,
    GenericErrorComponent,
    InvalidPasswordComponent,
    ModalAlertComponent,
    MultipleSessionsDetectedDialogComponent,
    NotRegisteredComponent,
    PdfModalComponent,

    // Pipes
    AddPaymentFeesPipe,

    // Engagements
    EngagementCompleteDialogComponent,
    EngagementErrorDialogComponent,
    EngagementsContainerComponent,
    ProvideOrDenyConsentDialogComponent,
  ],
	imports: [
		// Angular
		BrowserAnimationsModule,
		BrowserModule,
		CommonModule,
		FormsModule,
		ReactiveFormsModule,

		// Material
		MatAccordion,
		MatAutocompleteModule,
		MatButtonModule,
		MatCardModule,
		MatCheckboxModule,
		MatDatepickerModule,
		MatDialogModule,
		MatExpansionPanel,
		MatExpansionPanelHeader,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatMenuModule,
		MatNativeDateModule,
		MatPaginatorModule,
		MatProgressSpinnerModule,
		MatRadioModule,
		MatSelectModule,
		MatSidenavModule,
		MatSlideToggleModule,
		MatSortModule,
		MatTableModule,
		MatTabsModule,
		MatToolbarModule,
		MatTooltipModule,

		// Third-party
		ApolloModule,
		CurrencyMaskModule,
		DpDatePickerModule,
		NgxSpinnerModule,
		NgxsModule.forRoot(
			[AppState, SharedState, ConfigState, SettingsState, EngagementsState, CxpTasksState],
			{
				developmentMode: !environment.production,
				// Selectors otherwise compile their prop getters with `new Function(...)`,
				// which trips script-src 'unsafe-eval'.
				compatibility: { strictContentSecurityPolicy: true },
			},
		),
		NgxsStoragePluginModule.forRoot({
			keys: ['adminSso'],
		}),
		NgxsReduxDevtoolsPluginModule.forRoot({ name: 'client-portal', disabled: environment.production }),
		PdfViewerModule,

		// Foundation
		AppRoutingModule,

		// Aiwyn Libraries
		AuthModule,
		CommonLayoutModule,
		CommonSettingsModule,
		ConfigModule,
		EngagementsRoutingModule,
		PaymentsModule,
		QuickPaymentLinkErrorPageComponent,
		SharedModule,

		// Engagements
		HighlightPipe,
		ConsentStepComponent,
		EngagementCardItemComponent,
		EngagementsCardListComponent,
		EngagementsTableComponent,
		EngagementLetterStepComponent,
		PaymentInfoStepComponent,
		ViewEngagementContainerComponent,
		MatDivider,
		NgxMaskDirective,
	],
  providers: [
    ENVIRONMENT_PROVIDER,
    Title,
    PercentPipe,
    TitleCasePipe,
    { provide: ErrorHandler, useClass: CacheErrorHandler },
    // Lets lower-level shared services (e.g. the DMS upload retry) force a fresh
    // Firebase token without importing `@aiwyn/auth` (which would cycle).
    {
      provide: TOKEN_REFRESHER,
      useFactory: (auth: AuthService) => () => auth.refreshToken(),
      deps: [AuthService],
    },
    {
      // Intercept Auth calls
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      // Intercept Auth calls
      provide: HTTP_INTERCEPTORS,
      useClass: XhrInterceptor,
      multi: true,
    },
    {
      // Intercept API calls and call redirect to the login page if an error occurs
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(InitService));
      return initializerFn();
    }),
    // Instantiate eagerly so it can self-bootstrap once config values load; returns
    // nothing, so app startup is not blocked.
    provideAppInitializer(() => void inject(ZendeskWidgetService)),
    {
      provide: DeviceDetectorService,
      useClass: UniversalDeviceDetectorService,
    },
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: Apollo,
      useClass: ExtendedApollo,
      deps: [ConfigService, Globals, HttpLink, AuthProviderToken, Logger, LaunchDarklyService, NgZone],
    },
    PercentPipe,
    CurrencyPipe,
    DatePipe,
    { provide: APP_VERSION_TOKEN, useValue: packageJson.version },
    {
      provide: CUSTOMER_ASSET_URL,
      useFactory: (assetUriService: AssetUriService) => assetUriService.customerAssetUrl || '',
      deps: [AssetUriService],
    },
    provideLogging(),
    provideTracking({ enable: [PENDO_TRACKER, LOG_ROCKET_TRACKER] }),
    provideAuthWorkflowTracking(),
    providePaymentsWorkflowTracking(),
    provideSessionReplayCorrelation(),
    {
      provide: AbstractUploadApiService,
      useClass: UploadApiService,
    },
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { enterAnimationDuration: 0 },
    },
    provideHttpClient(withInterceptorsFromDi()),
    ...AUTH_PROVIDER_BINDINGS,
    provideFirebaseApp(() => initializeFirebaseApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  // Define services here that load at app startup
  constructor(
    private iconRegistry: MatIconRegistry,
    private readonly iconRegistryService: IconRegistryService,
    private sanitizer: DomSanitizer,
    private readonly logger: Logger,
    private usersnapService: UsersnapService, // While not used explicitly, this ensures the service is initialized
  ) {
    this.logger.info(`AppModule :: constructor :: App version: ${packageJson.version}`);
    this.registerProviderIcons();
    this.iconRegistryService.registerIcons();
  }

  registerProviderIcons() {
    this.iconRegistry
      .addSvgIcon('google-colored', this.sanitizer.bypassSecurityTrustResourceUrl('google.svg'));
    // .addSvgIcon('google', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/mdi/google.svg'))
    // .addSvgIcon('facebook', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/mdi/facebook.svg'))
    // .addSvgIcon('twitter', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/mdi/twitter.svg'))
    // .addSvgIcon('github', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/mdi/github-circle.svg'))
    // .addSvgIcon('microsoft', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/mdi/microsoft.svg'))
    // .addSvgIcon('yahoo', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/mdi/yahoo.svg'))
    // .addSvgIcon('phone', this.sanitizer.bypassSecurityTrustResourceUrl('/assets/phone.svg'));
  }
}
